import React, { useContext, useState, useEffect } from 'react';
import { ShopContext } from '../Context/ShopContext';
import { useNavigate } from 'react-router-dom';
import { toast, Toaster } from 'react-hot-toast';
import API_BASE_URL from '../config';
import { 
  FaTruck, 
  FaBox, 
  FaCheckCircle, 
  FaExclamationCircle,
  FaHome,
  FaBriefcase,
  FaShoppingBag,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaCity,
  FaGlobeAsia
} from 'react-icons/fa';
import { MdPayment, MdLocationOn } from 'react-icons/md';
import './Checkout.css';
import { getOptimizedImageUrl, setOptimizedImageFallback } from '../utils/imageOptimization';

const Checkout = () => {
    const { 
        getTotalCartAmount, 
        getCartItemsDetails, 
        clearCart
    } = useContext(ShopContext);
    const navigate = useNavigate();
    
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        district: '',
        postalCode: '',
        country: 'Nepal',
        addressType: 'home'
    });
    
    const [paymentMethod] = useState('COD'); // Only COD now
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    // Load saved shipping info
    useEffect(() => {
        const savedShippingInfo = localStorage.getItem('shippingInfo');
        if (savedShippingInfo) {
            try {
                const parsedInfo = JSON.parse(savedShippingInfo);
                setFormData(prev => ({
                    ...prev,
                    ...parsedInfo,
                    name: parsedInfo.name || parsedInfo.fullName || '',
                    address: parsedInfo.address || parsedInfo.street || ''
                }));
                toast.success('Loaded saved shipping information!', {
                    icon: <FaCheckCircle />,
                });
            } catch (error) {
            }
        }
    }, []);

    const cartItemsDetails = getCartItemsDetails();
    const totalAmount = getTotalCartAmount();

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.name.trim()) {
            newErrors.name = 'Full name is required';
        } else if (formData.name.trim().length < 2) {
            newErrors.name = 'Name must be at least 2 characters';
        }
        
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }
        
        if (!formData.phone.trim()) {
            newErrors.phone = 'Phone number is required';
        } else if (!/^[0-9]{10}$/.test(formData.phone.replace(/\D/g, ''))) {
            newErrors.phone = 'Please enter a valid 10-digit phone number';
        }
        
        if (!formData.address.trim()) {
            newErrors.address = 'Address is required';
        } else if (formData.address.trim().length < 5) {
            newErrors.address = 'Please enter a complete address';
        }
        
        if (!formData.city.trim()) {
            newErrors.city = 'City is required';
        }
        
        if (!formData.district.trim()) {
            newErrors.district = 'District is required';
        }
        
        if (!formData.postalCode.trim()) {
            newErrors.postalCode = 'Postal code is required';
        } else if (!/^[0-9]{5}$/.test(formData.postalCode)) {
            newErrors.postalCode = 'Please enter a valid 5-digit postal code';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            toast.error('Please fill all required fields correctly', {
                icon: <FaExclamationCircle />,
            });
            return;
        }

        if (cartItemsDetails.length === 0) {
            toast.error('Your cart is empty!', {
                icon: <FaExclamationCircle />,
            });
            return;
        }

        setLoading(true);

        try {
            const shippingInfo = {
                name: formData.name.trim(),
                email: formData.email.trim(),
                phone: formData.phone.trim(),
                address: formData.address.trim(),
                city: formData.city.trim(),
                district: formData.district.trim(),
                postalCode: formData.postalCode.trim(),
                country: formData.country,
                addressType: formData.addressType
            };

            // Prepare items array with product details INCLUDING COLOR
            const items = cartItemsDetails.map(item => ({
                productId: item.product.id,
                name: item.product.name,
                price: item.product.new_price,
                quantity: item.quantity,
                size: item.size || 'One Size',
                color: item.color || 'Not specified',
                image: item.product.main_image || item.product.image,
                category: item.product.category || 'general',
                productType: item.product.productType || 'clothing'
            }));

            const response = await fetch(`${API_BASE_URL}/api/order/createorder`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': localStorage.getItem('auth-token') || ''
                },
                body: JSON.stringify({
                    items: items,
                    shippingInfo: shippingInfo,
                    paymentMethod: paymentMethod,
                    totalAmount: totalAmount
                })
            });

            const data = await response.json();

            if (data.success) {
                // Save shipping info
                const saveInfo = {
                    ...formData,
                    fullName: formData.name,
                    street: formData.address
                };
                localStorage.setItem('shippingInfo', JSON.stringify(saveInfo));
                
                // Clear cart silently (no toast notification)
                clearCart();
                
                // Show order success toast
                toast.success(
                    <div className="success-toast-content">
                        <div className="toast-header">
                            <FaCheckCircle className="toast-icon" />
                            <span>Order Confirmed!</span>
                        </div>
                        <p>Your order #{data.orderId} has been placed successfully.</p>
                        <p className="toast-subtext">Redirecting to order confirmation...</p>
                    </div>,
                    {
                        duration: 2000,
                        position: 'top-center',
                        style: {
                            background: 'linear-gradient(135deg, #ff6b6b 0%, #ff4141 100%)',
                            color: 'white',
                            borderRadius: '10px',
                            padding: '20px'
                        }
                    }
                );
                
                // Redirect to order success page
                setTimeout(() => {
                    navigate('/order-success', { 
                        state: { 
                            orderId: data.orderId || data.orderId,
                            shippingInfo: shippingInfo,
                            totalAmount: totalAmount
                        } 
                    });
                }, 2000);
                
            } else {
                const errorMsg = data.message || data.error || 'Failed to place order';
                toast.error(errorMsg, {
                    icon: <FaExclamationCircle />,
                    duration: 4000,
                });
            }
        } catch (error) {
            toast.error('Network error. Please check your connection and try again.', {
                icon: <FaExclamationCircle />,
                duration: 4000,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSaveForLater = () => {
        localStorage.setItem('shippingInfo', JSON.stringify(formData));
        toast.success('Shipping information saved!', {
            icon: <FaCheckCircle />,
            duration: 2000,
        });
    };

    if (cartItemsDetails.length === 0) {
        return (
            <div className="checkout-empty">
                <Toaster />
                <div className="empty-cart-icon">
                    <FaShoppingBag />
                </div>
                <h2>Your cart is empty</h2>
                <p>Please add items to your cart before checkout.</p>
                <button 
                    onClick={() => navigate('/')}
                    className="continue-shopping-btn"
                >
                    <FaShoppingBag /> Continue Shopping
                </button>
            </div>
        );
    }

    return (
        <div className="checkout">
            <Toaster />
            <div className="checkout-container">
                <div className="checkout-left">
                    <div className="section-header">
                        <h2>
                            <FaUser className="section-icon" />
                            Delivery Information
                        </h2>
                        <button 
                            type="button" 
                            className="save-info-btn"
                            onClick={handleSaveForLater}
                        >
                            <FaCheckCircle /> Save Info
                        </button>
                    </div>
                    
                    <form onSubmit={handleSubmit} className="checkout-form">
                        <div className="form-group">
                            <label>
                                <FaUser className="input-icon" />
                                Full Name *
                            </label>
                            <input
                                type="text"
                                placeholder='Eg: Ram Kumar Thapa'
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                className={errors.name ? 'error' : ''}
                                maxLength={100}
                            />
                            {errors.name && <span className="error-message">{errors.name}</span>}
                        </div>

                        <div className="form-group">
                            <label>
                                <FaEnvelope className="input-icon" />
                                Email Address *
                            </label>
                            <input
                                type="email"
                                name="email"
                                placeholder='Eg: ajit@gmail.com'
                                value={formData.email}
                                onChange={handleInputChange}
                                className={errors.email ? 'error' : ''}
                            />
                            {errors.email && <span className="error-message">{errors.email}</span>}
                        </div>

                        <div className="form-group">
                            <label>
                                <FaPhone className="input-icon" />
                                Phone Number *
                            </label>
                            <input
                                type="tel"
                                name="phone"
                                placeholder='Eg: 9741760000'
                                value={formData.phone}
                                onChange={handleInputChange}
                                className={errors.phone ? 'error' : ''}
                                maxLength={10}
                            />
                            {errors.phone && <span className="error-message">{errors.phone}</span>}
                        </div>

                        <div className="form-group">
                            <label>
                                <MdLocationOn className="input-icon" />
                                Address *
                            </label>
                            <input
                                type="text"
                                name="address"
                                placeholder='Eg: Hulak Marga, Ward No. 5'
                                value={formData.address}
                                onChange={handleInputChange}
                                className={errors.address ? 'error' : ''}
                            />
                            {errors.address && <span className="error-message">{errors.address}</span>}
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>
                                    <FaCity className="input-icon" />
                                    City *
                                </label>
                                <input
                                    type="text"
                                    name="city"
                                    placeholder='Eg: Kathmandu'
                                    value={formData.city}
                                    onChange={handleInputChange}
                                    className={errors.city ? 'error' : ''}
                                />
                                {errors.city && <span className="error-message">{errors.city}</span>}
                            </div>

                            <div className="form-group">
                                <label>
                                    <FaMapMarkerAlt className="input-icon" />
                                    District *
                                </label>
                                <input
                                    type="text"
                                    name="district"
                                    placeholder='Eg: Bagmati'
                                    value={formData.district}
                                    onChange={handleInputChange}
                                    className={errors.district ? 'error' : ''}
                                />
                                {errors.district && <span className="error-message">{errors.district}</span>}
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>
                                    <FaMapMarkerAlt className="input-icon" />
                                    Postal Code *
                                </label>
                                <input
                                    type="text"
                                    name="postalCode"
                                    placeholder='Eg: 46021'
                                    value={formData.postalCode}
                                    onChange={handleInputChange}
                                    className={errors.postalCode ? 'error' : ''}
                                    maxLength={5}
                                />
                                {errors.postalCode && <span className="error-message">{errors.postalCode}</span>}
                            </div>

                            <div className="form-group">
                                <label>
                                    <FaGlobeAsia className="input-icon" />
                                    Country
                                </label>
                                <input
                                    type="text"
                                    name="country"
                                    value={formData.country}
                                    onChange={handleInputChange}
                                    disabled
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Address Type</label>
                            <div className="address-type-options">
                                <label className={`address-type-option ${formData.addressType === 'home' ? 'selected' : ''}`}>
                                    <input
                                        type="radio"
                                        name="addressType"
                                        value="home"
                                        checked={formData.addressType === 'home'}
                                        onChange={handleInputChange}
                                    />
                                    <FaHome className="option-icon" />
                                    <span>Home</span>
                                </label>
                                <label className={`address-type-option ${formData.addressType === 'work' ? 'selected' : ''}`}>
                                    <input
                                        type="radio"
                                        name="addressType"
                                        value="work"
                                        checked={formData.addressType === 'work'}
                                        onChange={handleInputChange}
                                    />
                                    <FaBriefcase className="option-icon" />
                                    <span>Work</span>
                                </label>
                            </div>
                        </div>

                        <div className="payment-method">
                            <h3>
                                <MdPayment className="section-icon" />
                                Payment Method
                            </h3>
                            <div className="payment-options">
                                <label className="payment-option selected">
                                    <input
                                        type="radio"
                                        name="payment"
                                        value="COD"
                                        checked={true}
                                        readOnly
                                    />
                                    <FaTruck className="payment-icon" />
                                    <div className="payment-details">
                                        <span className="payment-title">Cash on Delivery [COD]</span>
                                        <span className="payment-description">Pay when you receive your order</span>
                                    </div>
                                </label>
                            </div>
                        </div>

                        <div className="terms-agreement">
                            <label className="terms-checkbox">
                                <input type="checkbox" required />
                                <span>I agree to the Terms & Conditions and Privacy Policy</span>
                            </label>
                        </div>

                        <button 
                            type="submit" 
                            disabled={loading} 
                            className={`place-order-btn ${loading ? 'loading' : ''}`}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner"></span>
                                    Processing Order...
                                </>
                            ) : (
                                <>
                                    <FaCheckCircle className="btn-icon" />
                                    Place Order & Pay on Delivery
                                </>
                            )}
                        </button>
                    </form>
                </div>

                <div className="checkout-right">
                    <h2>
                        <FaShoppingBag className="section-icon" />
                        Order Summary
                    </h2>
                    <div className="order-items">
                        {cartItemsDetails.map((item) => (
                            <div key={item.cartKey} className="order-item">
                                <img 
                                    src={getOptimizedImageUrl(item.product.main_image || item.product.image, { width: 160, quality: 70 })} 
                                    alt={item.product.name} 
                                    loading="lazy"
                                    decoding="async"
                                    fetchpriority="low"
                                    width="80"
                                    height="80"
                                    onError={(e) => {
                                        setOptimizedImageFallback(e, item.product.main_image || item.product.image, 'https://via.placeholder.com/80x80?text=Product');
                                    }}
                                />
                                <div className="item-details">
                                    <h4>{item.product.name}</h4>
                                    <p><FaBox className="detail-icon" /> Size: {item.size || 'One Size'}</p>
                                    <p><FaBox className="detail-icon" /> Color: {item.color || 'Not specified'}</p>
                                    <p><FaShoppingBag className="detail-icon" /> Qty: {item.quantity}</p>
                                    <p className="item-price">Rs {item.product.new_price} × {item.quantity}</p>
                                </div>
                                <div className="item-total">
                                    Rs {item.product.new_price * item.quantity}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="order-total">
                        <div className="total-row">
                            <span>Subtotal:</span>
                            <span>Rs {totalAmount}</span>
                        </div>
                        <div className="total-row">
                            <span>
                                <FaTruck className="total-icon" />
                                Delivery Fee:
                            </span>
                            <span className="free-delivery">Free</span>
                        </div>
                        <div className="total-row final-total">
                            <span>Total Amount:</span>
                            <span className="total-amount">Rs {totalAmount}</span>
                        </div>
                    </div>

                    <div className="shipping-preview">
                        <h3>
                            <FaTruck className="section-icon" />
                            Shipping to:
                        </h3>
                        {formData.name && (
                            <div className="shipping-details-preview">
                                <p><FaUser className="preview-icon" /> <strong>{formData.name}</strong></p>
                                <p><MdLocationOn className="preview-icon" /> {formData.address}</p>
                                <p><FaCity className="preview-icon" /> {formData.city}, {formData.district}</p>
                                <p><FaMapMarkerAlt className="preview-icon" /> {formData.postalCode}, {formData.country}</p>
                                <p><FaPhone className="preview-icon" /> {formData.phone}</p>
                            </div>
                        )}
                    </div>
                    
                    <div className="delivery-info">
                        <div className="delivery-card">
                            <FaTruck className="delivery-card-icon" />
                            <div className="delivery-card-content">
                                <h4>Cash on Delivery</h4>
                                <p>Pay when you receive your order. Our delivery agent will collect the payment.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Checkout;

